interface EmarsysSettings {
  webPushApplicationCode?: string
  webPushNotificationIcon?: string
  webPushApplicationServerPublicKey?: string
  webPushPopupShowFrequency: number
}

const DEFAULT_POPUP_SHOW_FREQUENCY = 7

class EmarsysWebPushPopup extends HTMLElement {
  private canPrompt: boolean = false
  private userResponse: "allowed" | "denied" | null = null
  private isSubscribed: boolean = false
  private settings: EmarsysSettings = {
    webPushPopupShowFrequency: DEFAULT_POPUP_SHOW_FREQUENCY,
  }

  private readonly STORAGE_KEY_USER_RESPONSE = "emarsys-web-push-user-response"
  private readonly STORAGE_KEY_DISMISS_TIMESTAMP =
    "emarsys-web-push-dismiss-timestamp"

  constructor() {
    super()
    this.checkStoredResponse()
  }

  connectedCallback() {
    this.settings = {
      webPushApplicationCode: this.dataset.webPushApplicationCode,
      webPushNotificationIcon: this.dataset.webPushNotificationIcon,
      webPushApplicationServerPublicKey:
        this.dataset.webPushApplicationServerPublicKey,
      webPushPopupShowFrequency: this.dataset.webPushPopupShowFrequency
        ? Number(this.dataset.webPushPopupShowFrequency)
        : DEFAULT_POPUP_SHOW_FREQUENCY,
    }

    this.initEmarsysSDK()
  }

  async waitForEmarsysSdk(): Promise<void> {
    return new Promise((resolve) => {
      const checkSdk = () => {
        if ("WebEmarsysSdk" in globalThis) {
          resolve()
        } else {
          setTimeout(checkSdk, 1000)
        }
      }
      checkSdk()
    })
  }

  async initEmarsysSDK() {
    await this.waitForEmarsysSdk()

    console.debug(
      "[emarsys web push] Initializing SDK",
      "Settings:",
      this.settings
    )

    WebEmarsysSdk.push([
      "init",
      {
        enableLogging: true,
        applicationCode: this.settings?.webPushApplicationCode,
        defaultNotificationTitle: "Viktoria & Woods",
        defaultNotificationIcon:
          location.origin + this.settings?.webPushNotificationIcon,
        autoSubscribe: false,
        serviceWorker: {
          url: window.SHOPIFY_DATA.emarsys.serviceWorkerUrl,
          applicationServerPublicKey:
            this.settings?.webPushApplicationServerPublicKey,
        },
      },
    ])

    WebEmarsysSdk.push(() => {
      console.debug("[emarsys web push] SDK is ready")
      this.handleOnReady()
    })

    WebEmarsysSdk.push([
      "onSWInitError",
      () => {
        // Ignored
      },
    ])

    WebEmarsysSdk.push([
      "onSubscribe",
      () => {
        console.debug("[emarsys web push] onSubscribe event")
        this.isSubscribed = true
      },
    ])

    WebEmarsysSdk.push([
      "onUnsubscribe",
      () => {
        console.debug("[emarsys web push] onUnsubscribe event")
        this.userResponse = "denied"
        this.isSubscribed = false
        this.clearStoredResponse()
      },
    ])

    WebEmarsysSdk.push([
      "onPermissionDenied",
      () => {
        console.debug("[emarsys web push] onPermissionDenied event")
        this.userResponse = "denied"
      },
    ])

    WebEmarsysSdk.push([
      "onPermissionGranted",
      () => {
        console.debug("[emarsys web push] onPermissionGranted event")
        this.userResponse = "allowed"
      },
    ])
  }

  handleOnReady() {
    if (window?.SHOPIFY_DATA?.customer?.id) {
      this.login()
    } else {
      this.logout()
    }

    this.checkPromptStatus()
    this.setupEventListeners()
  }

  login() {
    if (!window?.SHOPIFY_DATA?.customer?.id) return
    WebEmarsysSdk.login({
      fieldId: 5344,
      fieldValue: window.SHOPIFY_DATA.customer.id,
    })
  }

  logout() {
    WebEmarsysSdk.logout()
  }

  checkStoredResponse() {
    // Check if user has already responded in storage
    const storedResponse = localStorage.getItem(this.STORAGE_KEY_USER_RESPONSE)
    if (storedResponse === "allowed" || storedResponse === "denied") {
      this.userResponse = storedResponse
    }
  }

  clearStoredResponse() {
    localStorage.removeItem(this.STORAGE_KEY_USER_RESPONSE)
    localStorage.removeItem(this.STORAGE_KEY_DISMISS_TIMESTAMP)
  }

  setupEventListeners() {
    this.querySelector(".allow-button")?.addEventListener(
      "click",
      this.handleAllow
    )
    this.querySelector(".deny-button")?.addEventListener(
      "click",
      this.handleDismiss
    )
  }

  handleAllow = async () => {
    this.clearStoredResponse()
    WebEmarsysSdk.subscribe()
    this.userResponse = "allowed"
    localStorage.setItem(this.STORAGE_KEY_USER_RESPONSE, this.userResponse)
    this.checkPromptStatus()
  }

  handleDismiss = () => {
    this.userResponse = "denied"
    localStorage.setItem(
      this.STORAGE_KEY_DISMISS_TIMESTAMP,
      new Date().getTime().toString()
    )
    this.checkPromptStatus()
  }

  /**
   * Determine if the popup should be shown
   */
  checkPromptStatus() {
    const storedDismissTimestamp = localStorage.getItem(
      this.STORAGE_KEY_DISMISS_TIMESTAMP
    )
    const lastPromptTimestamp = storedDismissTimestamp
      ? Number(storedDismissTimestamp)
      : 0

    const suppressionExpiryDate = new Date(
      lastPromptTimestamp +
        this.settings.webPushPopupShowFrequency * 24 * 60 * 60 * 1000
    )

    const suppressionExpired = new Date() > suppressionExpiryDate
    const userHasNotResponded = this.userResponse === null
    const isNotSubscribed = !this.isSubscribed
    this.canPrompt =
      userHasNotResponded && isNotSubscribed && suppressionExpired
    this.updatePopupVisibility()
  }

  updatePopupVisibility() {
    if (this.canPrompt) {
      this.classList.add("active")
    } else {
      this.classList.remove("active")
    }
  }
}

customElements.define("emarsys-web-push-popup", EmarsysWebPushPopup)
